/* eslint-disable react/prop-types */
import clsx from 'clsx'
import { Formik } from 'formik'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, Modal } from 'react-bootstrap'
import { FormattedMessage, injectIntl } from 'react-intl'
import Select from 'react-select'
import * as Yup from 'yup'
import { selectStyle } from '../../../../_augmentt/utils/styles'
import { updateApplication, updateMSPApplications } from '../../../crud/elastic.crud'
import ScrollMenu from '../../../partials/content/ScrollMenu'

const STATUS_LEVELS = [
  { value: 0, label: 'SAASCONFIG.UNKNOWN' },
  { value: 1, label: 'SAASCONFIG.RESTRICTED' },
  { value: 2, label: 'SAASCONFIG.PENDING_REVIEW' },
  { value: 3, label: 'SAASCONFIG.APPROVED' },
]

const RISK_PRODUCTIVITY_LEVELS = [
  { value: 0, label: 'SAASCONFIG.UNKNOWN' },
  { value: 1, label: 'SAASCONFIG.LOW' },
  { value: 2, label: 'SAASCONFIG.MEDIUM' },
  { value: 3, label: 'SAASCONFIG.HIGH' },
]

const formatOptions = (options, intl) =>
  options.map((option) => ({
    ...option,
    label: intl.formatMessage({ id: option.label }),
  }))

function EditApplicationModal(props) {
  const { show, onHide, onUpdate, intl, application, customer, categories, orgId } = props
  const [loading, setLoading] = useState(false)
  const [loadingButtonStyle, setLoadingButtonStyle] = useState({
    paddingLeft: '2.5rem',
    paddingRight: '2.5rem',
  })
  const [status, setStatus] = useState()
  const [categoryOptions, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState()
  const [selectedRiskLevel, setSelectedRiskLevel] = useState()
  const [selectedStatus, setSelectedStatus] = useState()
  const [selectedProductivityLevel, setSelectedProductivityLevel] = useState()

  const getApplicationValue = useCallback(
    (field) => application?.source?.[field] ?? application?.[field] ?? '',
    [application],
  )

  const hide = () => {
    onHide()
    setStatus()
  }

  const update = (updatedApplication) => {
    onUpdate(updatedApplication)
  }

  const statusOptions = useMemo(() => formatOptions(STATUS_LEVELS, intl), [intl])

  const riskOptions = useMemo(() => formatOptions(RISK_PRODUCTIVITY_LEVELS, intl), [intl])

  const productivityOptions = useMemo(() => formatOptions(RISK_PRODUCTIVITY_LEVELS, intl), [intl])
  const schema = Yup.object({
    name: Yup.string()
      .min(
        2,
        intl.formatMessage({
          id: 'AUTH.VALIDATION.TOO_SHORT',
        }),
      )
      .max(
        50,
        intl.formatMessage({
          id: 'AUTH.VALIDATION.TOO_LONG',
        }),
      )
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        }),
      ),
    url: Yup.string()
      .trim()
      .lowercase() // eslint-disable-next-line no-useless-escape
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(https?:\/\/)?([\da-z\.-]+\.[a-z\.]{2,6}|[\d\.]+)([\/:?=&#]{1}[\da-z\.-]+)*[\/\?]?$/,
        intl.formatMessage({
          id: 'AUTH.VALIDATION.INVALID_URL',
        }),
      )
      .min(
        2,
        intl.formatMessage({
          id: 'AUTH.VALIDATION.TOO_SHORT',
        }),
      )
      .max(
        100,
        intl.formatMessage({
          id: 'AUTH.VALIDATION.TOO_LONG',
        }),
      )
      .required(
        intl.formatMessage({
          id: 'AUTH.VALIDATION.REQUIRED_FIELD',
        }),
      ),
  })

  const initialValues = {
    name: application?.source?.COMPANY_NAME || application?.COMPANY_NAME || '',
    url: application?.source?.URL || application?.URL || '',
    category: selectedCategory,
    risk: selectedRiskLevel,
    status: selectedStatus,
    productivity: selectedProductivityLevel,
  }

  useEffect(() => {
    const allCategories = []

    for (let i = 0; i < categories.length; i += 1) {
      allCategories.push({
        value: categories[i].label,
        label: categories[i].label,
      })
    }
    setCategories(allCategories)

    for (let i = 0; i < allCategories?.length; i += 1) {
      // SaaS Applicaitons
      if (application && application.source && application.source.CATEGORY === allCategories[i].value) {
        setSelectedCategory(allCategories[i])
      } else if (application && application.CATEGORY !== undefined && application.CATEGORY === allCategories[i].value) {
        // Application Details Modal
        setSelectedCategory(allCategories[i])
      }
    }

    const approvalStatus = getApplicationValue('APPROVAL_STATUS')
    const risk = getApplicationValue('RISK')
    const productivity = getApplicationValue('PRODUCTIVITY')

    setSelectedStatus(statusOptions[approvalStatus])
    setSelectedRiskLevel(riskOptions[risk])
    setSelectedProductivityLevel(productivityOptions[productivity])
  }, [application, categories, intl, getApplicationValue, statusOptions, riskOptions, productivityOptions])
  const enableLoading = () => {
    setLoading(true)
    setLoadingButtonStyle({ paddingRight: '3.5rem', paddingLeft: '2.5rem' })
  }

  const disableLoading = () => {
    setLoading(false)
    setLoadingButtonStyle({ paddingRight: '2.5rem', paddingLeft: '2.5rem' })
  }

  /**
   * Edit application
   * @param {*} application
   */
  // eslint-disable-next-line no-shadow
  async function editApplication(application) {
    try {
      let updateResponse
      if (!customer?.id) {
        updateResponse = await updateMSPApplications(application, customer, orgId)
      } else {
        updateResponse = await updateApplication(application, customer)
      }

      const isSuccessful = updateResponse?.data?.result === true || updateResponse?.data?.result === undefined
      if (!isSuccessful) {
        const backendError = updateResponse?.data?.error || 'Failed to update application'
        throw new Error(backendError)
      }

      setTimeout(() => {
        hide()
        update(application)
        disableLoading()
      }, 1000)
    } catch (e) {
      console.error(e.message)
      setStatus(
        <>
          <FormattedMessage id="GENERAL.ERROR" />
          <a href="https://support.augmentt.com" target="_blank" rel="noopener noreferrer">
            <FormattedMessage id="GENERAL.CONTACT_SUPPORT" />
          </a>
        </>,
      )
      disableLoading()
    }
  }
  /**
   * Submit handler
   * @param {*} values
   */
  async function handleSubmit(values) {
    enableLoading()
    let updatedApp
    if (customer) {
      updatedApp = {
        id: application.id,
        index: application?.index,
        identity: getApplicationValue('IDENTITY'),
        process: getApplicationValue('PROCESS'),
        companyName: values.name,
        url: values.url,
        risk: values.risk.value,
        approvalStatus: values.status.value,
        category: values.category?.value,
        productivity: values.productivity.value,
      }
    } else {
      updatedApp = {
        id: application.id,
        companyName: values.name,
        identity: getApplicationValue('IDENTITY'),
        process: getApplicationValue('PROCESS'),
        url: values.url,
        risk: values.risk.value,
        approvalStatus: values.status.value,
        category: values.category?.value,
        productivity: values.productivity.value,
      }
    }
    editApplication(updatedApp)
  }

  return (
    <Modal
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      onHide={hide}
      onSubmit={update}
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <FormattedMessage id="SAASCONFIG.EDIT_APPLICATION" />
        </Modal.Title>
      </Modal.Header>
      <Formik
        validationSchema={schema}
        enableReinitialize
        initialValues={initialValues}
        initialErrors={schema.isValidSync(initialValues)}
        onSubmit={(values) => handleSubmit(values)}
      >
        {({
          // eslint-disable-next-line no-shadow
          handleSubmit,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          values,
          errors,
          touched,
        }) => (
          <Form>
            <Modal.Body>
              <div className="row justify-content-center align-items-center">
                <Col md="12">
                  {status && (
                    <div role="alert" className={`alert alert-${errors ? 'danger' : 'success'}`}>
                      <div className="alert-text">{status}</div>
                    </div>
                  )}
                </Col>
                <Form.Group as={Col} md="12" controlId="name">
                  <Form.Label className="inline">
                    <FormattedMessage id="SAASCONFIG.SAAS_APPLICATION" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="inline"
                    name="name"
                    placeholder={intl.formatMessage({
                      id: 'PLACEHOLDER.ENTER_APPLICATION_NAME',
                    })}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.name && !!errors.name}
                    isValid={touched.name && !errors.name}
                    disabled
                  />
                  <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="12" controlId="url">
                  <Form.Label className="inline">
                    <FormattedMessage id="SAASCONFIG.SAAS_APP_URL" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="inline"
                    name="url"
                    placeholder={intl.formatMessage({
                      id: 'PLACEHOLDER.ENTER_APPLICATION_URL',
                    })}
                    value={values.url}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched('url', true)}
                    isInvalid={touched.url && !!errors.url}
                    isValid={touched.url && !errors.url}
                    disabled
                  />
                  <Form.Control.Feedback type="invalid">{errors.url}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="12" controlId="category">
                  <Form.Label className="inline">
                    <FormattedMessage id="SAASCONFIG.CATEGORY" />
                  </Form.Label>
                  <Select
                    id="categorySelect"
                    name="category"
                    className="inline"
                    styles={selectStyle}
                    options={categoryOptions}
                    value={values.category}
                    onChange={(value) => setFieldValue('category', value)}
                    onBlur={() => setFieldTouched('category', true)}
                    getOptionValue={(value) => value}
                    placeholder={intl.formatMessage({
                      id: 'PLACEHOLDER.SELECT_CATEGORY',
                    })}
                    components={{ MenuList: ScrollMenu }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.category}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="12" controlId="appStatus">
                  <Form.Label className="inline">
                    <FormattedMessage id="SAASCONFIG.APPROVAL_STATUS" />
                  </Form.Label>
                  <Select
                    id="statusSelect"
                    name="status"
                    className="inline"
                    styles={selectStyle}
                    options={statusOptions}
                    value={values.status}
                    onChange={(value) => setFieldValue('status', value)}
                    onBlur={() => setFieldTouched('status', true)}
                    isSearchable={false}
                    components={{ MenuList: ScrollMenu }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="12" controlId="risk">
                  <Form.Label className="inline">
                    <FormattedMessage id="SAASCONFIG.RISK" />
                  </Form.Label>
                  <Select
                    id="riskSelect"
                    name="risk"
                    className="inline"
                    styles={selectStyle}
                    options={riskOptions}
                    value={values.risk}
                    onChange={(value) => setFieldValue('risk', value)}
                    onBlur={() => setFieldTouched('risk', true)}
                    isSearchable={false}
                    components={{ MenuList: ScrollMenu }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.risk}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="12" controlId="productivity">
                  <Form.Label className="inline">
                    <FormattedMessage id="SAASCONFIG.PRODUCTIVITY" />
                  </Form.Label>
                  <Select
                    id="productivitySelect"
                    name="productivity"
                    className="inline"
                    styles={selectStyle}
                    options={productivityOptions}
                    value={values.productivity}
                    onChange={(value) => setFieldValue('productivity', value)}
                    onBlur={() => setFieldTouched('productivity', true)}
                    isSearchable={false}
                    components={{ MenuList: ScrollMenu }}
                  />
                  <Form.Control.Feedback type="invalid">{errors.productivity}</Form.Control.Feedback>
                </Form.Group>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button id="closeModalBtn" onClick={hide} variant="light">
                <FormattedMessage id="GENERAL.CANCEL" />
              </Button>
              <Button
                // eslint-disable-next-line react/jsx-no-bind
                onClick={handleSubmit}
                id="editApplicationSubmit"
                variant="primary"
                className={`btn-elevate adduser__btn-primary ${clsx({
                  'spinner spinner--right spinner--md spinner--light': loading,
                })}`}
                style={loadingButtonStyle}
              >
                <FormattedMessage id="GENERAL.SUBMIT" />
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default injectIntl(EditApplicationModal)
